import type { CSSProperties } from 'react';
import type { ComponentContentSchema, SlotGroupDefinition } from '@vibesites/runtime';
import {
  useSlotGroup,
  useCollectionBinding,
  usePropertyField,
  usePropertyAddressUrl,
  useSecondaryColor,
} from '@vibesites/runtime/react';
import { EditableSlotGroup, RichTextContent } from '@vibesites/runtime/react';
import { repliWhiteLogoUrl } from '@vibesites/assets';
import { FairHousingIcon, PetsAllowedIcon, WheelchairIcon } from '@vibesites/assets';
import { VibesitesImage } from '@vibesites/ui-primitives';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import type { IconDefinition } from '@fortawesome/fontawesome-svg-core';
import { faFacebookF, faInstagram } from '@fortawesome/free-brands-svg-icons';

// ============================================================================
// Helpers
// ============================================================================

function formatPhone(raw: string | null | undefined): string {
  if (!raw) return '';
  const digits = raw.replace(/\D/g, '');
  const d = digits.length === 11 && digits.startsWith('1') ? digits.slice(1) : digits;
  if (d.length !== 10) return raw;
  return `${d.slice(0, 3)}-${d.slice(3, 6)}-${d.slice(6)}`;
}

function toTelUrl(raw: string | null | undefined): string {
  if (!raw) return '';
  if (raw.startsWith('tel:')) return raw;
  const digits = raw.replace(/[^+\d]/g, '');
  return digits ? `tel:${digits}` : raw;
}

/**
 * Returns true when a color is "dark" (i.e. white text reads well on top of it).
 * Accepts #rgb / #rrggbb hex strings. Unknown/unparseable values are treated as
 * dark, since the footer background defaults to a dark brand color.
 */
function isDarkColor(color: string | null | undefined): boolean {
  if (!color) return true;
  const match = color.trim().match(/^#?([0-9a-f]{3}|[0-9a-f]{6})$/i);
  if (!match) return true;
  let hex = match[1];
  if (hex.length === 3) hex = hex.split('').map((c) => c + c).join('');
  const r = parseInt(hex.slice(0, 2), 16);
  const g = parseInt(hex.slice(2, 4), 16);
  const b = parseInt(hex.slice(4, 6), 16);
  // Perceived luminance (ITU-R BT.601), normalized to 0–1.
  const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
  return luminance < 0.5;
}

/**
 * Removes a trailing country segment (e.g. "USA", "United States") from an
 * address string so the footer shows just the street / city / state / zip.
 */
function stripCountry(address: string): string {
  return address
    .replace(/,?\s*(u\.?\s*s\.?\s*a\.?|united states(?:\s+of\s+america)?|u\.?\s*s\.?)\s*$/i, '')
    .replace(/,\s*$/, '')
    .trim();
}

function hasValue(val: string | null | undefined): val is string {
  if (!val) return false;
  const normalized = val.trim().toLowerCase();
  return normalized !== '' && normalized !== 'false' && normalized !== 'null' && normalized !== 'undefined' && normalized !== '0' && normalized !== 'closed';
}

function formatTime(raw: string): string {
  const trimmed = raw.trim();
  if (/[ap]m/i.test(trimmed)) {
    return trimmed.replace(/([ap]m)/i, (m) => m.toLowerCase());
  }
  const match = trimmed.match(/^(\d{1,2}):(\d{2})$/);
  if (!match) return trimmed;
  let hours = parseInt(match[1], 10);
  const minutes = match[2];
  const period = hours >= 12 ? 'pm' : 'am';
  if (hours === 0) hours = 12;
  else if (hours > 12) hours -= 12;
  return `${hours}:${minutes} ${period}`;
}

const DAY_ORDER = ['mon', 'tue', 'wed', 'thu', 'fri', 'sat', 'sun'];
const DAY_LABELS: Record<string, string> = {
  mon: 'Monday', tue: 'Tuesday', wed: 'Wednesday', thu: 'Thursday',
  fri: 'Friday', sat: 'Saturday', sun: 'Sunday',
};

interface OfficeHour {
  day?: string | null;
  open_time?: string | null;
  close_time?: string | null;
}

export interface DayHours {
  label: string;
  hours: string;
}

function normalizeDay(day: string): string {
  return day.trim().toLowerCase().slice(0, 3);
}

export function listOfficeHours(raw: OfficeHour[]): DayHours[] {
  // Index the source entries by weekday so days can be looked up regardless of
  // ordering or duplicates. Any entries with an unrecognized day value are
  // preserved and appended after the standard Mon–Sun rows.
  const byDay = new Map<string, OfficeHour>();
  const extras: OfficeHour[] = [];
  for (const entry of raw) {
    if (!hasValue(entry.day)) continue;
    const key = normalizeDay(entry.day);
    if (DAY_ORDER.includes(key)) {
      if (!byDay.has(key)) byDay.set(key, entry);
    } else {
      extras.push(entry);
    }
  }

  const rowFor = (label: string, entry: OfficeHour | undefined): DayHours => {
    const isClosed = !entry || !hasValue(entry.open_time) || !hasValue(entry.close_time);
    const hoursStr = isClosed
      ? 'Closed'
      : `${formatTime(entry!.open_time || '')} - ${formatTime(entry!.close_time || '')}`;
    return { label, hours: hoursStr };
  };

  // Always emit all seven weekdays — days missing from the source, or missing
  // open/close times, render as "Closed".
  const out: DayHours[] = DAY_ORDER.map((key) =>
    rowFor(DAY_LABELS[key], byDay.get(key)),
  );

  // Preserve any non-standard day values (rare, but avoids silently dropping
  // data) after the standard rows.
  for (const entry of extras) {
    out.push(rowFor(entry.day || '', entry));
  }
  return out;
}

// ============================================================================
// Slot Group Definitions
// ============================================================================

const logoSlotDef: SlotGroupDefinition = {
  label: 'Footer Logo',
  fields: {
    // No defaultField: the variant (white-mono / primary color / black-mono /
    // secondary color) is picked automatically at render based on the footer
    // background. An explicit editor pick overrides that auto-selection.
    image: { label: 'Logo Image', type: 'image', suggestedFields: ['property_mono_white_logo', 'property_primary_logo', 'property_mono_black_logo', 'property_secondary_logo'] },
    alt: { label: 'Alt Text', type: 'text', defaultValue: 'Community Logo' },
    url: { label: 'URL', type: 'url', defaultField: 'property_company_url' },
    _target: { label: 'Target', type: 'text', defaultValue: '_blank' },
  },
};

const getInTouchLinkDef: SlotGroupDefinition = {
  label: 'Get in Touch Button',
  fields: {
    url: { label: 'URL', type: 'url', defaultValue: '/contact' },
    label: { label: 'Button Text', type: 'text', defaultValue: 'Get in Touch' },
    _target: { label: 'Target', type: 'text', defaultValue: '_self' },
  },
};

const addressLinkDef: SlotGroupDefinition = {
  label: 'Address',
  fields: {
    text: { label: 'Address Text', type: 'text', defaultField: 'property_full_address', defaultValue: '123 Main Street, City, ST 00000' },
    url: { label: 'URL', type: 'url' },
    _target: { label: 'Target', type: 'text', defaultValue: '_blank' },
  },
};

const phoneLinkDef: SlotGroupDefinition = {
  label: 'Phone Link',
  fields: {
    text: { label: 'Phone Text', type: 'text', defaultField: 'property_call_tracking_phone' },
    url: { label: 'URL', type: 'url', defaultField: 'property_call_tracking_phone' },
    _target: { label: 'Target', type: 'text', defaultValue: '_self' },
  },
};

const managedByLogoDef: SlotGroupDefinition = {
  label: 'Managed By Logo',
  fields: {
    // No defaultField: the variant (white-mono / color / black-mono) is picked
    // automatically at render based on the footer background. An explicit editor
    // pick overrides that auto-selection.
    image: { label: 'Logo Image', type: 'image', suggestedFields: ['property_company_mono_white_logo', 'property_company_logo', 'property_company_mono_black_logo'] },
    alt: { label: 'Alt Text', type: 'text', defaultValue: 'Property Management Company Logo' },
    url: { label: 'URL', type: 'url', defaultField: 'property_company_url' },
    _target: { label: 'Target', type: 'text', defaultValue: '_blank' },
  },
};

const designByLogoDef: SlotGroupDefinition = {
  label: 'Website Design By Logo',
  fields: {
    alt: { label: 'Alt Text', type: 'text', defaultValue: 'Repli' },
    url: { label: 'URL', type: 'url', defaultValue: 'https://www.repli360.com/' },
    _target: { label: 'Target', type: 'text', defaultValue: '_blank' },
  },
};

const accessibilityLinkDef: SlotGroupDefinition = {
  label: 'Accessibility Link',
  fields: {
    text: { label: 'Link Text', type: 'text', defaultValue: 'Accessibility' },
    url: { label: 'URL', type: 'url', defaultValue: '/accessibility' },
    _target: { label: 'Target', type: 'text', defaultValue: '_self' },
  },
};

const privacyPolicyLinkDef: SlotGroupDefinition = {
  label: 'Privacy Policy Link',
  fields: {
    text: { label: 'Link Text', type: 'text', defaultValue: 'Privacy Policy' },
    url: { label: 'URL', type: 'url', defaultValue: '/privacy-policy' },
    _target: { label: 'Target', type: 'text', defaultValue: '_self' },
  },
};

const sitemapLinkDef: SlotGroupDefinition = {
  label: 'Sitemap Link',
  fields: {
    text: { label: 'Link Text', type: 'text', defaultValue: 'Sitemap' },
    url: { label: 'URL', type: 'url', defaultValue: '/sitemap' },
    _target: { label: 'Target', type: 'text', defaultValue: '_self' },
  },
};

const facebookLinkDef: SlotGroupDefinition = {
  label: 'Facebook',
  fields: {
    url: { label: 'URL', type: 'url', defaultField: 'property_facebook_url' },
    label: { label: 'Label', type: 'text', defaultValue: 'Facebook' },
    _target: { label: 'Target', type: 'text', defaultValue: '_blank' },
  },
};

const instagramLinkDef: SlotGroupDefinition = {
  label: 'Instagram',
  fields: {
    url: { label: 'URL', type: 'url', defaultField: 'property_instagram_url' },
    label: { label: 'Label', type: 'text', defaultValue: 'Instagram' },
    _target: { label: 'Target', type: 'text', defaultValue: '_blank' },
  },
};

// ============================================================================
// Social icon link — a plain icon (no circular container) for every platform.
// ============================================================================

interface SocialIconLinkProps {
  url: string | null | undefined;
  target: string | null | undefined;
  label: string;
  icon: IconDefinition;
}

function SocialIconLink({ url, target, label, icon }: SocialIconLinkProps) {
  return url ? (
    <a
      href={url}
      target={target || '_blank'}
      rel="noopener noreferrer"
      aria-label={label}
      className="footer-social-link"
    >
      <FontAwesomeIcon icon={icon} className="text-2xl" />
    </a>
  ) : (
    <span className="footer-social-link footer-social-link--disabled" aria-hidden="true">
      <FontAwesomeIcon icon={icon} className="text-2xl" />
    </span>
  );
}

// ============================================================================
// Component
// ============================================================================

export function Footer() {
  const callTrackingPhone = usePropertyField('property_call_tracking_phone') as string | null | undefined;
  const propertyPhone = usePropertyField('property_phone') as string | null | undefined;
  const propertyAddressUrl = usePropertyAddressUrl();

  // Management-company logo variants — the correct one is chosen from the footer
  // background luminance below.
  const companyColorLogo = usePropertyField('property_company_logo') as string | null | undefined;
  const companyWhiteLogo = usePropertyField('property_company_mono_white_logo') as string | null | undefined;
  const companyBlackLogo = usePropertyField('property_company_mono_black_logo') as string | null | undefined;

  // Community logo variants — same tone-aware selection as the managed-by logo.
  const propertyPrimaryLogo = usePropertyField('property_primary_logo') as string | null | undefined;
  const propertySecondaryLogo = usePropertyField('property_secondary_logo') as string | null | undefined;
  const propertyWhiteLogo = usePropertyField('property_mono_white_logo') as string | null | undefined;
  const propertyBlackLogo = usePropertyField('property_mono_black_logo') as string | null | undefined;

  const footerBgColor = useSecondaryColor();
  const footerBgIsDark = isDarkColor(footerBgColor);
  // Tone-aware logo selection. We deliberately never fall back to the
  // opposite-tone mono variant (black on a dark footer, white on a light
  // footer) — an invisible/low-contrast logo is worse than the color variant
  // or, ultimately, no logo at all. Color logos are permitted on either tone
  // since they're designed to read on both.
  const autoManagedByLogo = footerBgIsDark
    ? (companyWhiteLogo || companyColorLogo || '')
    : (companyColorLogo || companyBlackLogo || '');
  const autoCommunityLogo = footerBgIsDark
    ? (propertyWhiteLogo || propertyPrimaryLogo || propertySecondaryLogo || '')
    : (propertyPrimaryLogo || propertyBlackLogo || propertySecondaryLogo || '');

  // Tone-aware palette — flips text, borders, and mono icons/logos between a
  // white ramp (on dark backgrounds) and a near-black ramp (on light ones) so
  // the footer stays legible regardless of the secondary brand color.
  const footerToneStyle: CSSProperties = footerBgIsDark
    ? ({
        '--footer-fg': '#ffffff',
        '--footer-fg-muted': 'rgba(255, 255, 255, 0.9)',
        '--footer-fg-subtle': 'rgba(255, 255, 255, 0.8)',
        '--footer-fg-faint': 'rgba(255, 255, 255, 0.5)',
        '--footer-border': 'rgba(255, 255, 255, 0.2)',
        // Repli SVG ships white; keep it as-is on dark backgrounds.
        '--footer-repli-filter': 'none',
      } as CSSProperties)
    : ({
        '--footer-fg': '#111111',
        '--footer-fg-muted': 'rgba(17, 17, 17, 0.9)',
        '--footer-fg-subtle': 'rgba(17, 17, 17, 0.75)',
        '--footer-fg-faint': 'rgba(17, 17, 17, 0.5)',
        '--footer-border': 'rgba(17, 17, 17, 0.15)',
        // Invert the white Repli SVG to near-black so it reads on light bgs.
        '--footer-repli-filter': 'invert(1) brightness(0.15)',
      } as CSSProperties);

  // Slot groups
  const logo = useSlotGroup('footer.logo', logoSlotDef);
  const getInTouchLink = useSlotGroup('footer.get_in_touch_link', getInTouchLinkDef);
  const addressLink = useSlotGroup('footer.address', addressLinkDef);
  const phoneLink = useSlotGroup('footer.phone_link', phoneLinkDef);
  const managedByLogo = useSlotGroup('footer.managed_by_logo', managedByLogoDef);
  const designByLogo = useSlotGroup('footer.design_by_logo', designByLogoDef);
  const accessibilityLink = useSlotGroup('footer.accessibility_link', accessibilityLinkDef);
  const privacyPolicyLink = useSlotGroup('footer.privacy_policy_link', privacyPolicyLinkDef);
  const sitemapLink = useSlotGroup('footer.sitemap_link', sitemapLinkDef);
  const facebookLink = useSlotGroup('footer.facebook_link', facebookLinkDef);
  const instagramLink = useSlotGroup('footer.instagram_link', instagramLinkDef);

  // Office hours
  const officeHours = useCollectionBinding<OfficeHour>('footer.office_hours', {
    collection: 'office_hours',
  });
  const dayHours = officeHours && officeHours.length > 0
    ? listOfficeHours(officeHours)
    : [];

  // Derived URLs / display values
  const addressHref = addressLink.url || propertyAddressUrl || '#';
  const phoneHref = toTelUrl(phoneLink.url) || toTelUrl(callTrackingPhone) || toTelUrl(propertyPhone);
  const phoneDisplay = formatPhone(phoneLink.text) || formatPhone(callTrackingPhone) || formatPhone(propertyPhone) || '555-555-5555';

  const addressText = stripCountry(addressLink.text || '123 Main Street, City, ST 00000');
  const commaIdx = addressText.indexOf(',');
  const addressLine1 = commaIdx !== -1 ? addressText.slice(0, commaIdx).trim() : addressText;
  const addressLine2 = commaIdx !== -1 ? addressText.slice(commaIdx + 1).trim() : '';

  return (
    <footer
      className="footer-root"
      style={footerToneStyle}
      data-footer-tone={footerBgIsDark ? 'dark' : 'light'}
    >
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-14 md:py-20">

        {/* ============================================================ */}
        {/* HERO ROW — Heading + Phone + Get in Touch                   */}
        {/* ============================================================ */}
        <div className="flex flex-col items-center lg:flex-row lg:items-start lg:justify-between gap-8 mb-16 md:mb-24">
          <RichTextContent
            path="footer.lets_talk_heading"
            defaultValue="<p>LET'S TALK ABOUT YOUR NEW HOME!</p>"
            as="h2"
            className="footer-hero-heading font-heading font-extrabold uppercase leading-[1.05] text-3xl md:text-4xl lg:text-5xl max-w-xl text-center lg:text-left"
          />
          <div className="flex flex-wrap items-center justify-center lg:justify-start gap-6 lg:pt-2">
            <a
              href={phoneHref || '#'}
              className="footer-hero-phone font-heading text-xl md:text-2xl font-bold tracking-wide dniphone dniphonehref whitespace-nowrap hover:opacity-80 transition-opacity"
            >
              {phoneDisplay}
            </a>
            <EditableSlotGroup groupPath="footer.get_in_touch_link" definition={getInTouchLinkDef}>
              <a
                href={getInTouchLink.url || '/contact'}
                target={getInTouchLink._target || '_self'}
                className="footer-get-in-touch-btn"
              >
                {getInTouchLink.label || 'Get in Touch'}
              </a>
            </EditableSlotGroup>
          </div>
        </div>

        {/* ============================================================ */}
        {/* MAIN GRID — Logo / Contact / Office Hours / Follow Us       */}
        {/* ============================================================ */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-10 md:gap-8">

          {/* Logo */}
          <EditableSlotGroup groupPath="footer.logo" definition={logoSlotDef} className="text-center lg:text-left">
            {(() => {
              // Editor pick wins; otherwise the tone-aware auto-variant kicks in.
              const communityLogoSrc = logo.image || autoCommunityLogo;
              return (
                <a
                  href={logo.url || '/'}
                  target={logo.url ? logo._target || '_blank' : undefined}
                  rel={logo.url ? 'noopener noreferrer' : undefined}
                  className="inline-block"
                >
                  {communityLogoSrc ? (
                    <VibesitesImage
                      url={communityLogoSrc}
                      alt={logo.alt || 'Community Logo'}
                      sizes="240px"
                      className="h-20 w-auto max-w-[240px] object-contain"
                    />
                  ) : (
                    <div className="footer-fallback-logo font-heading text-2xl">Our Community</div>
                  )}
                </a>
              );
            })()}
          </EditableSlotGroup>

          {/* Contact us */}
          <div className="text-center">
            <RichTextContent
              path="footer.contact_label"
              defaultValue="<p>Contact us</p>"
              as="h3"
              className="font-heading text-xl md:text-2xl font-bold mb-4"
            />
            <EditableSlotGroup groupPath="footer.phone_link" definition={phoneLinkDef}>
              <a
                href={phoneHref || '#'}
                className="footer-link block font-body text-base md:text-lg dniphone dniphonehref"
              >
                {phoneDisplay}
              </a>
            </EditableSlotGroup>
            <EditableSlotGroup groupPath="footer.address" definition={addressLinkDef}>
              <a
                href={addressHref}
                target={addressLink._target || '_blank'}
                rel="noopener noreferrer"
                className="footer-link block mt-4 font-body text-base md:text-lg leading-snug"
              >
                <span className="block">{addressLine1}</span>
                {addressLine2 ? <span className="block">{addressLine2}</span> : null}
              </a>
            </EditableSlotGroup>
          </div>

          {/* Office Hours */}
          <div>
            <RichTextContent
              path="footer.office_hours_label"
              defaultValue="<p>Office Hours</p>"
              as="h3"
              className="font-heading text-xl md:text-2xl font-bold mb-4 text-center"
            />
            {dayHours.length > 0 ? (
              <dl className="space-y-1.5 sm:max-w-xs sm:mx-auto lg:max-w-none lg:mx-0">
                {dayHours.map((entry, index) => (
                  <div key={index} className="footer-hours-row flex justify-between gap-4 font-body text-sm md:text-base">
                    <dt>{entry.label}</dt>
                    <dd className="whitespace-nowrap">{entry.hours}</dd>
                  </div>
                ))}
              </dl>
            ) : (
              <p className="footer-hours-empty font-body text-sm text-center">Office hours will be displayed here.</p>
            )}
          </div>

          {/* Follow Us */}
          <div className="text-center">
            <RichTextContent
              path="footer.social_label"
              defaultValue="<p>Follow Us</p>"
              as="h3"
              className="font-heading text-xl md:text-2xl font-bold mb-4"
            />
            <div className="flex items-center justify-center gap-3">
              <EditableSlotGroup groupPath="footer.facebook_link" definition={facebookLinkDef}>
                <SocialIconLink
                  url={facebookLink.url}
                  target={facebookLink._target}
                  label={facebookLink.label || 'Facebook'}
                  icon={faFacebookF}
                />
              </EditableSlotGroup>
              <EditableSlotGroup groupPath="footer.instagram_link" definition={instagramLinkDef}>
                <SocialIconLink
                  url={instagramLink.url}
                  target={instagramLink._target}
                  label={instagramLink.label || 'Instagram'}
                  icon={faInstagram}
                />
              </EditableSlotGroup>
            </div>
          </div>

        </div>
      </div>

      {/* ============================================================ */}
      {/* Divider                                                      */}
      {/* ============================================================ */}
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <hr className="footer-divider border-t" />
      </div>

      {/* ============================================================ */}
      {/* Bottom Bar — Managed By / Legal Links / Designed By          */}
      {/* ============================================================ */}
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex flex-col md:flex-row items-center justify-between gap-6">

          {/* Professionally Managed By — left */}
          <div className="flex flex-col items-center md:items-start gap-2">
            <RichTextContent
              path="footer.managed_by_label"
              defaultValue="<p>Professionally Managed By</p>"
              as="span"
              className="footer-caption font-body text-xs"
            />
            <EditableSlotGroup groupPath="footer.managed_by_logo" definition={managedByLogoDef}>
              {(() => {
                const managedLogoSrc = managedByLogo.image || autoManagedByLogo;
                return managedLogoSrc ? (
                  <a href={managedByLogo.url || '#'} target={managedByLogo._target || '_blank'} rel="noopener noreferrer" className="opacity-90 hover:opacity-100 transition-opacity">
                    <VibesitesImage
                      url={managedLogoSrc}
                      alt={managedByLogo.alt || 'Property Management Company'}
                      sizes="160px"
                      className="h-9 w-auto max-w-[160px] object-contain"
                    />
                  </a>
                ) : null;
              })()}
            </EditableSlotGroup>
          </div>

          {/* Legal links — center */}
          <nav className="footer-legal flex flex-wrap items-center justify-center gap-x-3 gap-y-1 font-body text-sm" aria-label="Footer links">
            <EditableSlotGroup groupPath="footer.accessibility_link" definition={accessibilityLinkDef}>
              <a
                href={accessibilityLink.url || '/accessibility'}
                target={accessibilityLink._target || '_self'}
                className="footer-legal-link"
              >
                {accessibilityLink.text || 'Accessibility'}
              </a>
            </EditableSlotGroup>
            <span className="footer-legal-sep" aria-hidden="true">•</span>
            <EditableSlotGroup groupPath="footer.privacy_policy_link" definition={privacyPolicyLinkDef}>
              <a
                href={privacyPolicyLink.url || '/privacy-policy'}
                target={privacyPolicyLink._target || '_self'}
                className="footer-legal-link"
              >
                {privacyPolicyLink.text || 'Privacy Policy'}
              </a>
            </EditableSlotGroup>
            <span className="footer-legal-sep" aria-hidden="true">•</span>
            <EditableSlotGroup groupPath="footer.sitemap_link" definition={sitemapLinkDef}>
              <a
                href={sitemapLink.url || '/sitemap'}
                target={sitemapLink._target || '_self'}
                className="footer-legal-link"
              >
                {sitemapLink.text || 'Sitemap'}
              </a>
            </EditableSlotGroup>
          </nav>

          {/* Website Designed By — right */}
          <div className="flex flex-col items-center md:items-end gap-2">
            <RichTextContent
              path="footer.design_by_label"
              defaultValue="<p>Website Designed By</p>"
              as="span"
              className="footer-caption font-body text-xs"
            />
            <EditableSlotGroup groupPath="footer.design_by_logo" definition={designByLogoDef}>
              <a href={designByLogo.url || 'https://www.repli360.com/'} target={designByLogo._target || '_blank'} rel="noopener noreferrer" className="opacity-90 hover:opacity-100 transition-opacity">
                <VibesitesImage
                  url={repliWhiteLogoUrl}
                  alt={designByLogo.alt || 'Repli'}
                  height={30}
                  sizes="120px"
                  className="footer-repli-logo h-8 w-auto"
                  priority
                />
              </a>
            </EditableSlotGroup>
          </div>

        </div>

        {/* Compliance icons */}
        <div className="footer-compliance flex items-center justify-center gap-10 md:gap-14 pt-8">
          <FairHousingIcon size={40} aria-label="Equal Housing Opportunity" />
          <PetsAllowedIcon size={30} aria-label="Pet Friendly" />
          <WheelchairIcon size={30} aria-label="Wheelchair Accessible" />
        </div>
      </div>

      {/* ============================================================ */}
      {/* Scoped Styles                                                */}
      {/* ============================================================ */}
      <style>{`
        .footer-root {
          background-color: var(--vibe-color-secondary, #1a1a1a);
          /* Root text color follows the tone palette so descendants inherit
             the correct color unless they override it explicitly. */
          color: var(--footer-fg, #fff);
        }
        .footer-fallback-logo {
          color: var(--footer-fg, #fff);
        }
        .footer-link {
          color: var(--footer-fg-muted, rgba(255, 255, 255, 0.9));
          transition: color 0.25s ease;
        }
        .footer-link:hover {
          color: var(--footer-fg, #fff);
        }
        .footer-hours-row {
          color: var(--footer-fg-muted, rgba(255, 255, 255, 0.9));
        }
        .footer-hours-empty {
          color: var(--footer-fg-faint, rgba(255, 255, 255, 0.5));
        }
        .footer-divider {
          border-color: var(--footer-border, rgba(255, 255, 255, 0.2));
        }
        .footer-caption {
          color: var(--footer-fg-subtle, rgba(255, 255, 255, 0.8));
        }
        .footer-legal {
          color: var(--footer-fg-muted, rgba(255, 255, 255, 0.9));
        }
        .footer-legal-link {
          color: inherit;
          transition: color 0.25s ease;
        }
        .footer-legal-link:hover {
          color: var(--footer-fg, #fff);
        }
        .footer-legal-sep {
          /* Matches the surrounding link color so the dots have proper contrast
             on both light and dark footers (0.5-opacity dots were readable on
             a dark bg but washed out on a light one). */
          color: var(--footer-fg-muted, rgba(255, 255, 255, 0.9));
        }
        .footer-compliance {
          color: var(--footer-fg, #fff);
        }
        .footer-repli-logo {
          filter: var(--footer-repli-filter, none);
        }
        .footer-get-in-touch-btn {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          padding: 0.85rem 2rem;
          font-family: var(--vibe-font-heading, var(--vibe-font-body, sans-serif));
          font-size: 0.8rem;
          font-weight: 700;
          letter-spacing: 0.14em;
          text-transform: uppercase;
          color: var(--vibe-color-text-on-primary, #fff);
          background-color: var(--vibe-color-primary);
          text-decoration: none;
          transition: opacity 0.25s ease;
        }
        .footer-get-in-touch-btn:hover {
          opacity: 0.85;
        }
        .footer-social-link {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          font-size: 1.5rem;
          color: var(--footer-fg, #fff);
          transition: color 0.25s ease, opacity 0.25s ease;
        }
        a.footer-social-link:hover {
          color: var(--footer-fg, #fff);
          opacity: 0.8;
        }
        .footer-social-link--disabled {
          opacity: 1;
        }
      `}</style>

    </footer>
  );
}

export const contentSchema: ComponentContentSchema = {
  slot: 'Footer',
  displayName: 'Footer',
  tokens: {
    'footer.lets_talk_heading': {
      label: 'Let\'s Talk Heading',
      type: 'richtext',
      defaultValue: '<p>LET\'S TALK ABOUT YOUR NEW HOME!</p>',
    },
    'footer.contact_label': {
      label: 'Contact Label',
      type: 'richtext',
      defaultValue: '<p>Contact us</p>',
    },
    'footer.office_hours_label': {
      label: 'Office Hours Label',
      type: 'richtext',
      defaultValue: '<p>Office Hours</p>',
    },
    'footer.social_label': {
      label: 'Social Media Label',
      type: 'richtext',
      defaultValue: '<p>Follow Us</p>',
    },
    'footer.managed_by_label': {
      label: 'Managed By Label',
      type: 'richtext',
      defaultValue: '<p>Professionally Managed By</p>',
    },
    'footer.design_by_label': {
      label: 'Designed By Label',
      type: 'richtext',
      defaultValue: '<p>Website Designed By</p>',
    },
  },
  slotGroups: {
    'footer.logo': logoSlotDef,
    'footer.get_in_touch_link': getInTouchLinkDef,
    'footer.address': addressLinkDef,
    'footer.phone_link': phoneLinkDef,
    'footer.managed_by_logo': managedByLogoDef,
    'footer.design_by_logo': designByLogoDef,
    'footer.accessibility_link': accessibilityLinkDef,
    'footer.privacy_policy_link': privacyPolicyLinkDef,
    'footer.sitemap_link': sitemapLinkDef,
    'footer.facebook_link': facebookLinkDef,
    'footer.instagram_link': instagramLinkDef,
  },
  collectionBindings: {
    'footer.office_hours': {
      label: 'Office Hours',
      collection: 'office_hours',
    },
  },
};

(Footer as any).contentSchema = contentSchema;

export default Footer;
